import { useCallback, useEffect, useRef, useState } from "react";
import fetchWithCredentials from "apiClient";
import { subscribeContactChatSocket } from "utils/contactChatSocket";

export type ChecklistStepStatus = "pending" | "completed" | "not_applicable";

export type ChecklistStep = {
  id: string;
  scope: "account" | "condo";
  status: ChecklistStepStatus;
  titleEn: string;
  titleEs: string;
  canMarkNa: boolean;
  canMarkCompleted: boolean;
  priority: number;
  isSuggestedNext: boolean;
};

export type ChecklistProgress = {
  completed: number;
  total: number;
  percent: number;
};

export type OnboardingChecklist = {
  condoId: number | null;
  progress: ChecklistProgress;
  suggestedNextId: string | null;
  steps: ChecklistStep[];
  managerContactId: number | null;
};

type GuideResponse = {
  status: string;
  stepId: string;
  guided: boolean;
  managerContactId: number;
  conversationId: number;
};

/** Collapse bursts of chat/entity websocket events into one refetch. */
const REFRESH_DEBOUNCE_MS = 500;

function isChecklistIncomplete(checklist: OnboardingChecklist | null): boolean {
  if (!checklist) {
    return false;
  }
  const { completed, total } = checklist.progress;
  return total > 0 && completed < total;
}

export function useOnboardingChecklist(condoId: number | null) {
  const [checklist, setChecklist] = useState<OnboardingChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [guiding, setGuiding] = useState(false);
  const previousStatusesRef = useRef<Record<string, ChecklistStepStatus>>({});
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const params = new URLSearchParams();
    if (condoId != null) {
      params.set("condo", String(condoId));
    }
    const query = params.toString();
    const url = `/manager-api/onboarding-checklist/${query ? `?${query}` : ""}`;
    try {
      const data = (await fetchWithCredentials(url)) as OnboardingChecklist;
      if (requestId !== requestIdRef.current) {
        return data;
      }
      setChecklist(data);
      setLoading(false);
      return data;
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
      throw error;
    }
  }, [condoId]);

  useEffect(() => {
    setLoading(true);
    setChecklist(null);
    previousStatusesRef.current = {};
    refresh().catch(() => undefined);
  }, [refresh]);

  const managerContactId = checklist?.managerContactId ?? null;
  const incomplete = isChecklistIncomplete(checklist);

  // Event-driven refresh instead of the previous 8s poll that dominated
  // backend load. Refetch when the manager's contact websocket fires
  // (checklist overrides, entity changes, chat turns) or when the tab
  // becomes visible again. Keep the socket only while onboarding is open.
  useEffect(() => {
    if (managerContactId == null) {
      return;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refresh().catch(() => undefined);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (!incomplete) {
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    }

    let debounceTimer: number | undefined;
    const scheduleRefresh = () => {
      // Hidden tabs already refetch on visibilitychange; skip background GETs.
      if (document.hidden) {
        return;
      }
      if (debounceTimer !== undefined) {
        window.clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = undefined;
        if (document.hidden) {
          return;
        }
        refresh().catch(() => undefined);
      }, REFRESH_DEBOUNCE_MS);
    };

    const unsubscribe = subscribeContactChatSocket(managerContactId, () => {
      scheduleRefresh();
    });

    return () => {
      if (debounceTimer !== undefined) {
        window.clearTimeout(debounceTimer);
      }
      unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [managerContactId, incomplete, refresh]);

  const consumeStatusChanges = useCallback(() => {
    if (!checklist) {
      return [] as ChecklistStep[];
    }
    const changed: ChecklistStep[] = [];
    const nextMap: Record<string, ChecklistStepStatus> = {};
    for (const step of checklist.steps) {
      nextMap[step.id] = step.status;
      const prev = previousStatusesRef.current[step.id];
      if (
        prev &&
        prev !== step.status &&
        (step.status === "completed" || step.status === "not_applicable")
      ) {
        changed.push(step);
      }
    }
    // Seed without toasts on first load.
    if (Object.keys(previousStatusesRef.current).length === 0) {
      previousStatusesRef.current = nextMap;
      return [];
    }
    previousStatusesRef.current = nextMap;
    return changed;
  }, [checklist]);

  const guideStep = useCallback(
    async (stepId: string) => {
      setGuiding(true);
      try {
        const body: { step_id: string; condo_id?: number } = {
          step_id: stepId,
        };
        if (condoId != null) {
          body.condo_id = condoId;
        }
        const data = (await fetchWithCredentials(
          "/manager-api/onboarding-checklist/guide/",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        )) as GuideResponse;
        await refresh();
        return data;
      } finally {
        setGuiding(false);
      }
    },
    [condoId, refresh]
  );

  return {
    checklist,
    loading,
    guiding,
    refresh,
    guideStep,
    consumeStatusChanges,
  };
}
